import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InnerLayout from '../../components/layouts/innerLayout';
import InputField from '../../components/forms/InputField';
import SelectDropdown from '../../components/forms/selectDropDown';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { errorToast } from '../../components/toast';
import { customersListAction, generateBillAction, productDropdownAction } from '../../redux/actions/billingAction';
import { accountDetailsAction } from '../../redux/actions/authAction';
import LoaderModal from '../../components/modals/loaderModal';

// Validation Schema
const validationSchema = Yup.object({
    // customerPhone: Yup.string().required('Required'),
    // customerName: Yup.string().required('Required'),
    payment_method: Yup.string().required('Required'),
});

const PosBilling = () => {
    const [productOptions, setProductOptions] = useState([])
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const barcodeInputRef = useRef(null);
    const tableRef = useRef(null);
    const [customers, setCustomers] = useState([]);
    const [debouncedValue, setDebouncedValue] = useState("");
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(searchValue);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchValue]);

    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

    useEffect(() => {
        barcodeInputRef.current?.focus();
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchAccount()
    }, [])

    useEffect(() => {
        fetchCustomers()
    }, [debouncedValue])

    const fetchProducts = () => {
        setLoading(true)
        dispatch(productDropdownAction({}, onProdFetchSuccess, onError))
    }

    const fetchAccount = () => {
        setLoading(true)
        dispatch(accountDetailsAction({}, (data) => { setAccountData(data.data.data) }, onError))
    }

    const fetchCustomers = () => {
        dispatch(customersListAction({ search: debouncedValue }, onCustomersFetchSuccess, onError))
    }

    const onCustomersFetchSuccess = (data) => {
        let updated = data?.data?.data?.map((customer) => {
            return {
                id: customer.id,
                name: customer.name,
                phone: customer.phone
            }
        })
        setCustomers(updated)
        setLoading(false);
    }

    const onProdFetchSuccess = (data) => {

        const updatedProds = data.data.data.map((prod) => {
            let obj = {
                value: prod?.id,
                label: prod?.barcode ? `${prod?.name}-${prod?.barcode}` : prod?.name,
                price: prod?.price,
                tax: prod?.tax,
                discount: prod?.discount,
                hsnCode: prod?.hsnCode,
                barcode: prod?.barcode
            }
            return obj;
        });
        setProductOptions(updatedProds);
        setLoading(false)
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    const handleProductChange = (index, field, value, setFieldValue, values) => {
        const updatedProducts = [...values.products];
        if (!updatedProducts[index]) return;
        updatedProducts[index][field] = value;

        const product = updatedProducts[index];

        const price = parseFloat(product.price) || 0; // Price including tax
        const quantity = parseInt(product.quantity, 10) || 1;
        const discount = parseFloat(product.discount) || 0;
        const taxRate = parseFloat(product.tax) || parseFloat(accountData.default_tax) || 0;

        const { discountedPrice, cgst, igst, sgst, productPrice, tax, totalPrice, totalTax, unitPrice } = calculatePriceDetails(price, taxRate, discount, accountData.tax_with_product, accountData.gstEnabled, accountData.transaction_in, quantity)

        // Update the product with tax and pricing
        updatedProducts[index].tax = parseFloat(tax);
        updatedProducts[index].discountedPrice = parseFloat(discountedPrice);
        updatedProducts[index].productPrice = parseFloat(productPrice);
        updatedProducts[index].cgst = parseFloat(cgst);
        updatedProducts[index].sgst = parseFloat(sgst);
        updatedProducts[index].igst = parseFloat(igst);
        updatedProducts[index].totalTax = parseFloat(totalTax);
        updatedProducts[index].unitPrice = parseFloat(unitPrice);
        updatedProducts[index].totalPrice = parseFloat(totalPrice);


        // Update the products in the form
        setFieldValue('products', updatedProducts);

        // Add a new product row if necessary
        if (index === updatedProducts.length - 1 && product.id !== '') {
            addProductRow(setFieldValue, values);
        }
    };

    const addProductRow = (setFieldValue, values) => {
        const newProduct = {
            id: '',
            name: '',
            quantity: 1,
            hsnCode: '',
            price: 0,
            discount: 0,
            tax: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalTax: 0,
            total: 0,
            productPrice: 0,
            totalPrice: 0
        };
        setFieldValue('products', [...values.products, newProduct]); // Add a new product row
        barcodeInputRef.current?.focus();
    };

    const removeProductRow = (index, setFieldValue, products) => {
        const updatedProducts = products.filter((_, i) => i !== index);
        setFieldValue('products', updatedProducts);
    };

    const calculateSummary = (products) => {
        let subtotal = 0;
        let totalCgst = 0;
        let totalSgst = 0;
        let totalIgst = 0;
        let grandTotal = 0;

        products.forEach(product => {
            subtotal += product.productPrice * product.quantity;
            totalCgst += product.cgst * product.quantity;
            totalSgst += product.sgst * product.quantity;
            totalIgst += product.igst * product.quantity;
            grandTotal += product.totalPrice
        });

        // const grandTotal = subtotal + totalCgst + totalSgst + totalIgst;

        return {
            totalGst: totalCgst + totalSgst + totalIgst,
            subtotal: subtotal.toFixed(2),
            totalCgst: totalCgst.toFixed(2),
            totalSgst: totalSgst.toFixed(2),
            totalIgst: totalIgst.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
        };
    };

    const calculatePriceDetails = (price, tax, discount, taxOnProduct, gstEnabled, transactionIn, quantity) => {
        // Parse inputs to ensure proper calculation
        price = parseFloat(price) || 0;
        tax = gstEnabled ? parseFloat(tax) || 0 : 0;
        discount = parseFloat(discount) || 0;
        quantity = parseInt(quantity, 10) || 1;

        // Calculate discounted price
        const discountedPrice = price - (price * discount / 100);

        let cgst = 0, sgst = 0, igst = 0, totalTax = 0, productPrice = discountedPrice;

        if (gstEnabled) {
            if (taxOnProduct === "inclusive") {
                // Tax-inclusive: Calculate the base price (price without tax)
                productPrice = discountedPrice / (1 + (tax / 100));
                totalTax = discountedPrice - productPrice;

            } else {
                // Tax-exclusive: Calculate tax based on the discounted price
                productPrice = discountedPrice;
                totalTax = (discountedPrice * tax) / 100;
            }

            // Split the tax based on transaction type
            if (transactionIn === "intraState") {
                cgst = totalTax / 2;
                sgst = totalTax / 2;
            } else if (transactionIn === "interState") {
                igst = totalTax;
            }
        }

        // Calculate unit price (base price + total tax) and total price
        const unitPrice = productPrice + totalTax;
        const totalPrice = unitPrice * quantity;

        return {
            price,
            discount,
            discountedPrice: discountedPrice.toFixed(2),
            tax,
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            totalTax: totalTax.toFixed(2),
            productPrice: productPrice.toFixed(2),
            unitPrice: unitPrice.toFixed(2),
            totalPrice: totalPrice.toFixed(2),
        };
    }

    const handleSubmit = (values) => {
        let updatedProds = values.products.filter(prod => prod.id);
        values.products = updatedProds;
        if (updatedProds.length < 1) {
            errorToast("Atleast one product required")
        } else {
            dispatch(generateBillAction(values, onSuccess, onError))
        }
    };

    const onSuccess = (data) => {
        navigate('/invoice', { state: { invoiceData: data.data.data } })
    }

    const handleBarcodeScan = (e, setFieldValue, values) => {
        let barcode = e.target.value;
        console.log("scanned value", barcode);
        console.log("event type", e.key);
        console.log("event", e);
        if (e.key === "ArrowDown") {
            console.log("Scanned barcode:", barcode);
            const scannedProduct = productOptions.find(product => String(product.barcode) === String(barcode));
            if (scannedProduct) {
                const existingIndex = values.products.findIndex(item => item.id === scannedProduct.value);

                if (existingIndex !== -1) {
                    const newQuantity = values.products[existingIndex].quantity + 1;
                    handleProductChange(existingIndex, 'quantity', newQuantity, setFieldValue, values);
                } else {
                    // If product doesn't exist, add a new product
                    const newIndex = values.products.length - 1;  // Use the length to add a new product at the end
                    handleProductChange(newIndex, 'id', scannedProduct.value, setFieldValue, values);
                    handleProductChange(newIndex, 'price', scannedProduct.price, setFieldValue, values);
                    handleProductChange(newIndex, 'tax', scannedProduct.tax, setFieldValue, values);
                    handleProductChange(newIndex, 'discount', scannedProduct.discount, setFieldValue, values);
                    handleProductChange(newIndex, 'hsnCode', scannedProduct.hsnCode, setFieldValue, values);
                }
            } else {
                alert("Product not found!");
            }
            if (barcodeInputRef.current) {
                barcodeInputRef.current.value = "";
            }
        }
    };

    return (
        <InnerLayout module={'POS Billing'}>
            <div className='p-4'>
                <Formik
                    initialValues={{
                        customerPhone: '',
                        customerName: '',
                        payment_method: '',
                        products: [{
                            id: '', name: '', quantity: 1, price: 0, discount: 0, tax: 0, cgst: 0, sgst: 0, hsnCode: '',
                            igst: 0, totalTax: 0, total: 0, productPrice: 0, totalPrice: 0
                        }],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ setFieldValue, values }) => (
                        <Form className="bg-gray-100" style={{ height: '80vh' }}>
                            <div className="bg-white shadow rounded p-4 max-h-[85%] h-full">
                                {/* Customer Details */}
                                <div className="flex flex-wrap gap-1 md:gap-4 mb-2 md:mb-0 sticky top-0 z-20 bg-white">
                                    <input
                                        type="text"
                                        autoFocus
                                        className="absolute opacity-0"
                                        onKeyDown={(e) => handleBarcodeScan(e, setFieldValue, values)}
                                        ref={barcodeInputRef}
                                    />
                                    <div className="relative" style={{ width: "100%", maxWidth: "20rem" }}>
                                        <InputField
                                            value={values.customerPhone}
                                            label="Customer Phone"
                                            name="customerPhone"
                                            type="text"
                                            placeholder="Enter customer phone"
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                            onFocus={() => setShowCustomerDropdown(true)}
                                            onChange={(e) => {
                                                setFieldValue("customerPhone", e.target.value);
                                                setSearchValue(e?.target?.value);
                                                setShowCustomerDropdown(true); // Keep dropdown open while typing
                                            }}
                                            onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 100)}
                                        />
                                        {showCustomerDropdown && customers.length > 0 && (
                                            <ul className="absolute z-30 bg-white border border-gray-300 w-full mt-1 rounded shadow-lg max-h-48 overflow-y-auto">
                                                {customers.map((customer) => (
                                                    <li
                                                        key={customer.id}
                                                        className="px-4 py-2 cursor-pointer hover:bg-blue-400 text-start"
                                                        onMouseDown={() => { // Use onMouseDown to trigger before onBlur
                                                            setFieldValue("customerPhone", customer.phone);
                                                            setFieldValue("customerName", customer.name);
                                                            setShowCustomerDropdown(false);
                                                            setSearchValue(''); // Clear search term after selection
                                                        }}
                                                    >
                                                        {customer.name} ({customer.phone})
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div>
                                        <InputField
                                            value={values.customerName}
                                            label="Customer Name"
                                            name="customerName"
                                            type="text"
                                            placeholder="Enter customer name"
                                            style={{ width: "100%", maxWidth: "20rem" }}
                                        />
                                    </div>
                                    <div className="mx-2 z-20" style={{ width: "100%", maxWidth: "15rem" }}>
                                        <SelectDropdown
                                            label={"Payment Method"}
                                            options={[
                                                { value: 'cash', label: "Cash" },
                                                { value: "online", label: "Online" },
                                                { value: "card", label: "Card", }
                                            ]}
                                            value={values.payment_method}
                                            onChange={(name, value, selectedOption) => {
                                                setFieldValue("payment_method", value);
                                            }}
                                            placeholder="Select payment method"
                                            name="payment_method"
                                            className="w-full z-10"
                                        />
                                    </div>
                                </div>

                                {/* Product Table */}
                                <div ref={tableRef} className="overflow-x-auto h-full max-h-[85%]">
                                    <table className="w-full border-collapse border text-sm">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="bg-gray-100">
                                                <th className="border px-2 py-1">S No.</th>
                                                <th className="border px-2 py-1">Product Name</th>
                                                <th className="border px-2 py-1">Quantity</th>
                                                <th className="border px-2 py-1">Hsncode</th>
                                                <th className="border px-2 py-1">Price</th>
                                                <th className="border px-2 py-1">Discount (%)</th>
                                                <th className="border px-2 py-1">Discounted Price</th>
                                                {accountData?.gstEnabled &&
                                                    <>
                                                        <th className="border px-2 py-1">Tax (%)</th>
                                                        <th className="border px-2 py-1">CGST</th>
                                                        <th className="border px-2 py-1">SGST</th>
                                                        <th className="border px-2 py-1">IGST</th>
                                                        <th className="border px-2 py-1">Total Tax</th>
                                                    </>
                                                }
                                                <th className="border px-2 py-1">Unit Price</th>
                                                <th className="border px-2 py-1">Total</th>
                                                <th className="border px-2 py-1">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {values?.products.map((product, index) => (
                                                <tr key={index} id={`product-row-${index}`}>
                                                    <td className="border px-2 py-1 text-center">{index + 1}</td>
                                                    <td className="border px-2 py-1 w-64">
                                                        <div style={{ width: "100%", minWidth: '12rem', maxWidth: "15rem" }}>
                                                            <SelectDropdown
                                                                options={productOptions}
                                                                value={product?.id}
                                                                onChange={(name, value, selectedOption) => {
                                                                    handleProductChange(index, 'id', value, setFieldValue, values); // Pass values to handleProductChange                                                          
                                                                    if (selectedOption) {
                                                                        handleProductChange(index, 'price', selectedOption.price, setFieldValue, values);
                                                                        handleProductChange(index, 'tax', selectedOption.tax, setFieldValue, values);
                                                                        handleProductChange(index, 'discount', selectedOption.discount, setFieldValue, values);
                                                                        handleProductChange(index, 'hsnCode', selectedOption.hsnCode, setFieldValue, values);
                                                                    }
                                                                }}
                                                                placeholder="Select product"
                                                                name={`products[${index}].id`}
                                                                onMenuOpen={() => {
                                                                    setTimeout(() => {
                                                                        if (tableRef.current) {
                                                                            tableRef.current.scrollTo({
                                                                                top: tableRef.current.scrollHeight,
                                                                                behavior: 'smooth'
                                                                            });
                                                                        }
                                                                    }, 50);
                                                                }}

                                                            />
                                                        </div>

                                                    </td>
                                                    <td className="border px-2">
                                                        <InputField
                                                            value={product.quantity}
                                                            name={`products[${index}].quantity`}
                                                            type="number"
                                                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value, setFieldValue, values)}
                                                            style={{ width: "100%", minWidth: '6rem', maxWidth: "6rem", marginTop: '1rem' }}
                                                        />
                                                    </td>
                                                    <td className="border px-2">

                                                        {product.hsnCode || "--"}

                                                    </td>
                                                    <td className="border px-2 py-1">

                                                        {product?.price}
                                                    </td>
                                                    <td className="border py-1">

                                                        {product?.discount}
                                                    </td>
                                                    <td className="border py-1">

                                                        {product?.discountedPrice?.toFixed(2)}
                                                    </td>
                                                    {accountData?.gstEnabled &&
                                                        <>
                                                            <td className="border py-1">
                                                                {product?.tax?.toFixed(2)}
                                                            </td>
                                                            <td className="border py-1">
                                                                {product?.cgst?.toFixed(2)}
                                                            </td>
                                                            <td className="border py-1">
                                                                {product?.sgst?.toFixed(2)}
                                                            </td>
                                                            <td className="border py-1">
                                                                {product?.igst?.toFixed(2)}
                                                            </td>
                                                            <td className="border px-2 py-1 text-right">{product?.totalTax?.toFixed(2)}</td>
                                                        </>
                                                    }
                                                    <td className="border px-2 py-1 text-right">
                                                        {product?.productPrice?.toFixed(2)}
                                                    </td>
                                                    <td className="border px-2 py-1 text-right">{product?.totalPrice?.toFixed(2)}</td>
                                                    {index !== values.products.length - 1 && (
                                                        <td className="border px-2 py-1 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => removeProductRow(index, setFieldValue, values.products)}
                                                                className="text-red-500 hover:underline"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="bg-white p-4 mt-4 shadow-2xl">
                                <div className="text-lg font-bold mb-4 text-start">Summary</div>
                                <div className="flex md:gap-16 text-sm text-nowrap flex-wrap">
                                    <div>
                                        <div className="flex gap-4">
                                            <span>Total CGST:</span>
                                            <span>₹ {calculateSummary(values.products).totalCgst}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Total SGST:</span>
                                            <span>₹ {calculateSummary(values.products).totalSgst}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Total IGST:</span>
                                            <span>₹ {calculateSummary(values.products).totalIgst}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex gap-4">
                                            <span>Total Tax:</span>
                                            <span>₹ {(calculateSummary(values.products).totalGst).toFixed(2)}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>Subtotal:</span>
                                            <span>₹ {calculateSummary(values.products).subtotal}</span>
                                        </div>
                                        <div className="flex gap-4 font-bold">
                                            <span>Grand Total:</span>
                                            <span>₹ {calculateSummary(values.products).grandTotal}</span>
                                        </div>
                                    </div>

                                </div>
                                <div className="flex justify-end mt-4">
                                    <button type="button" className="bg-gray-500 text-white px-4 py-2 rounded mr-2">
                                        Cancel
                                    </button>
                                    <button type="submit" className="bg-green-500 text-white px-4 py-2 rounded">
                                        Save and Print
                                    </button>
                                </div>
                            </div>
                            {loading &&
                                <LoaderModal />
                            }
                        </Form>
                    )}
                </Formik>
            </div>

        </InnerLayout >
    );
};

export default PosBilling;
